<script setup lang="ts">
import AccountsSection from '@/components/accountSettings/AccountsSection.vue'
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
  getMySettings,
  updateMySettings,
  listMcpTokens,
  createMcpToken,
  revokeMcpToken,
  MCP_SCOPES,
  ApiError,
  type MapProvider,
  type UserSettings,
  type McpToken,
  type McpTokenCreated,
} from '@/api'
import { useAuthStore } from '@/stores/auth'
import { MAP_PROVIDERS, mapKey } from '@/settings/mapProviders'
import TelegramIcon from '@/components/TelegramIcon.vue'
import { languageOptions } from '@/settings/languageOptions'
import type { BulletSettings } from '@/settings/listBullets'
import ListBulletsEditor from '@/components/ListBulletsEditor.vue'
import TagStylesEditor from '@/components/TagStylesEditor.vue'
import { setTagStyles, type TagStyle } from '@/todos/hashtags'
import { currentLocale, languageFlag, resolveLocale, setLocale } from '@/i18n'
import { getTelegramLanguage } from '@/telegramWebApp'
import PermissionCheckboxGroup from '@/components/PermissionCheckboxGroup.vue'
import CalendarExport from '@/components/CalendarExport.vue'
import NavList, { type NavGroup } from '@/components/NavList.vue'
import PageHeader from '@/components/PageHeader.vue'
import PostsSection from '@/components/PostsSection.vue'
import ProtocolHandlers from '@/components/ProtocolHandlers.vue'
import TagsSection from '@/components/tagSettings/TagsSection.vue'
import { formatDateTime } from '@/todos/dates'
import { clearListView, listViewPrefs } from '@/composables/useListViewPrefs'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useBackButton } from '@/composables/useBackButton'
import { goBack } from '@/router/goBack'
import { findSection, sectionsFor } from '@/settings/sections'
import { parseTagsPath, tagsParent } from '@/settings/tagsNav'
import { accountsParent, parseAccountsPath } from '@/settings/accountsNav'
import { parseSharingPath, sharingParent } from '@/settings/sharingNav'
import SharingSection from '@/components/sharingSettings/SharingSection.vue'
import {
  hasSubsections,
  parseSubsectionPath,
  subsectionParent,
  SETTINGS_SUBSECTIONS,
} from '@/settings/subsections'
import CommandLink from '@/components/CommandLink.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

const auth = useAuthStore()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()

// `/settings` lists the categories, `/settings/<key>` shows one of them.
const entries = computed(() => sectionsFor(auth.isAdmin))
const current = computed(() => findSection(route.params.section, auth.isAdmin))
// (the flag of the language in use is shown on the right of its row, so a wrong one is noticed)
const navGroups = computed<NavGroup[]>(() => [
  {
    key: 'settings',
    items: entries.value.map((entry) => ({
      to: `/settings/${entry.key}`,
      icon: entry.icon,
      title: t(entry.titleKey),
      value: entry.key === 'language' ? languageFlag(currentLocale()) : undefined,
    })),
  },
])
// A category that is a list of its own (Customisation, Debug) has its pages below it: `/settings/customisation/links`.
const subView = computed(() => {
  const key = current.value?.key
  return hasSubsections(key) ? parseSubsectionPath(key, route.params.rest) : undefined
})
const sub = computed(() => (subView.value?.view === 'page' ? subView.value.subsection : undefined))
const subGroups = computed<NavGroup[]>(() => {
  const key = current.value?.key
  if (!hasSubsections(key)) return []
  return [
    {
      key,
      items: SETTINGS_SUBSECTIONS[key].map((entry) => ({
        to: `/settings/${key}/${entry.key}`,
        icon: entry.icon,
        title: t(entry.titleKey),
        hint: t(entry.hintKey),
      })),
    },
  ]
})
watchEffect(() => {
  if (route.params.section && !current.value) void router.replace('/settings')
  else if (subView.value?.view === 'unknown') void router.replace(`/settings/${current.value?.key}`)
})
// Back goes one level up: the pages below a category (a tag and its sub pages) first lead to the category.
const backTarget = computed(() => {
  const key = current.value?.key
  if (key === 'tags') return tagsParent(parseTagsPath(route.params.rest))
  if (key === 'accounts') return accountsParent(parseAccountsPath(route.params.rest))
  if (key === 'blocked') return sharingParent(parseSharingPath(route.params.rest))
  if (hasSubsections(key) && subView.value) return subsectionParent(key, subView.value)
  return current.value ? '/settings' : '/todos'
})
useBackButton(() => goBack(router, backTarget.value))

const toast = useToast()
const settings = ref<UserSettings | null>(null)
const settingsLoading = ref(true)
const settingsError = ref('')
const settingsSaving = reactive<Record<string, boolean>>({})
const languageRows = computed(() => languageOptions(settings.value?.language_code ?? null))

const tokens = ref<McpToken[]>([])
const tokensLoading = ref(true)
const tokensError = ref('')
const tokenName = ref('')
const tokenDescription = ref('')
const tokenScopes = ref<string[]>([])
const tokenExpiresAt = ref('')
const tokenCreating = ref(false)
const tokenCreateError = ref('')
const revealedToken = ref<McpTokenCreated | null>(null)
const revokingId = ref<string | null>(null)

const scopeOptions = computed(() =>
  MCP_SCOPES.map((scope) => ({
    value: scope.value,
    label: t(`settings.tokens.scope.${scope.value}`),
  })),
)

async function loadSettings() {
  settingsLoading.value = true
  settingsError.value = ''
  try {
    settings.value = await getMySettings()
    setTagStyles(settings.value.tag_styles)
  } catch (e) {
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.load')
  } finally {
    settingsLoading.value = false
  }
}

type ToggleKey =
  | 'discoverable_by_username'
  | 'reveal_invite_failure_reason'
  | 'lightweight_confirmations'
  | 'verbose_sync_messages'
  | 'notify_new_todos'

async function toggleSetting(key: ToggleKey) {
  if (!settings.value) return
  const next = !settings.value[key]
  const previous = settings.value[key]
  settings.value[key] = next // optimistic
  settingsSaving[key] = true
  settingsError.value = ''
  try {
    settings.value = await updateMySettings({ [key]: next })
  } catch (e) {
    settings.value[key] = previous
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.update')
  } finally {
    settingsSaving[key] = false
  }
}

async function changeLanguage(value: string) {
  if (!settings.value) return
  settingsSaving.language_code = true
  settingsError.value = ''
  try {
    settings.value = await updateMySettings({ language_code: value })
    setLocale(resolveLocale(settings.value.language_code, getTelegramLanguage()))
  } catch (e) {
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.update')
  } finally {
    settingsSaving.language_code = false
  }
}

async function changeMapProvider(key: 'map_provider' | 'map_provider_telegram', event: Event) {
  if (!settings.value) return
  const value = (event.target as HTMLSelectElement).value as MapProvider | ''
  settingsSaving[key] = true
  settingsError.value = ''
  try {
    settings.value = await updateMySettings({ [key]: value })
  } catch (e) {
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.update')
  } finally {
    settingsSaving[key] = false
  }
}

async function changeTagStyles(styles: TagStyle[]) {
  if (!settings.value) return
  settingsSaving.tag_styles = true
  settingsError.value = ''
  try {
    settings.value = await updateMySettings({ tag_styles: styles })
    setTagStyles(settings.value.tag_styles)
  } catch (e) {
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.update')
  } finally {
    settingsSaving.tag_styles = false
  }
}

async function changeBullets(changes: Partial<BulletSettings>) {
  if (!settings.value) return
  settingsSaving.list_bullets = true
  settingsError.value = ''
  try {
    settings.value = await updateMySettings(changes)
  } catch (e) {
    settingsError.value = e instanceof ApiError ? e.message : t('settings.err.update')
  } finally {
    settingsSaving.list_bullets = false
  }
}

async function resetListView() {
  settingsSaving.list_view = true
  try {
    await clearListView()
    toast.info(t('settings.listView.cleared'))
  } catch (e) {
    toast.error(e, t('settings.err.update'))
    reportError(e, 'settings.err.update')
  } finally {
    settingsSaving.list_view = false
  }
}

async function loadTokens() {
  tokensLoading.value = true
  tokensError.value = ''
  try {
    tokens.value = await listMcpTokens()
  } catch (e) {
    tokensError.value = e instanceof ApiError ? e.message : t('settings.err.loadTokens')
  } finally {
    tokensLoading.value = false
  }
}

async function submitCreateToken() {
  tokenCreateError.value = ''
  tokenCreating.value = true
  try {
    const created = await createMcpToken({
      name: tokenName.value,
      description: tokenDescription.value || null,
      scopes: tokenScopes.value,
      expires_at: tokenExpiresAt.value ? new Date(tokenExpiresAt.value).toISOString() : null,
    })
    revealedToken.value = created
    tokenName.value = ''
    tokenDescription.value = ''
    tokenScopes.value = []
    tokenExpiresAt.value = ''
    await loadTokens()
  } catch (e) {
    tokenCreateError.value = e instanceof ApiError ? e.message : t('settings.err.createToken')
  } finally {
    tokenCreating.value = false
  }
}

async function revoke(token: McpToken) {
  revokingId.value = token.id
  try {
    const updated = await revokeMcpToken(token.id)
    tokens.value = tokens.value.map((t) => (t.id === updated.id ? updated : t))
  } catch (e) {
    tokensError.value = e instanceof ApiError ? e.message : t('settings.err.revokeToken')
  } finally {
    revokingId.value = null
  }
}

async function copyToken() {
  if (!revealedToken.value) return
  try {
    await navigator.clipboard.writeText(revealedToken.value.token)
  } catch {
    // clipboard unavailable -- the token is still shown on screen to copy by hand
  }
}

onMounted(() => {
  void loadSettings()
  if (auth.isAdmin) void loadTokens()
})
</script>

<template>
  <div :class="{ single: current }">
    <PageHeader
      v-if="current"
      :title="sub ? t(sub.titleKey) : t(current.titleKey)"
      :back-to="backTarget"
      :back-label="t('settings.back')"
    />
    <PageHeader v-else :title="t('settings.title')" />

    <NavList v-if="!current" :groups="navGroups" />
    <NavList v-else-if="subView?.view === 'list'" :groups="subGroups" />

    <section v-if="current?.key === 'language'" class="card">
      <h3>{{ t('settings.language.title') }}</h3>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <template v-else-if="settings">
        <UiGroup>
          <UiList>
            <UiRow
              as="button"
              v-for="option in languageRows"
              :key="option.code"
              type="button"
              class="language-row"
              :aria-pressed="option.selected"
              :disabled="settingsSaving.language_code"
              @click="changeLanguage(option.code)"
            >
              <span class="language-icon" aria-hidden="true">
                <TelegramIcon v-if="!option.code" />
                <template v-else>{{ option.flag }}</template>
              </span>
              <span class="entry-title">{{
                option.code ? option.name : t('settings.language.auto')
              }}</span>
              <span v-if="option.selected" class="check" aria-hidden="true">✓</span>
            </UiRow>
          </UiList>
        </UiGroup>
        <i18n-t keypath="settings.language.hint" tag="p" class="hint">
          <template #command><CommandLink command="setlang" /></template>
        </i18n-t>
      </template>
    </section>

    <section v-if="sub?.key === 'maps'" class="card">
      <h3>{{ t('settings.maps.title') }}</h3>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <template v-else-if="settings">
        <label>
          {{ t('settings.maps.label') }}
          <select
            :value="settings.map_provider"
            :disabled="settingsSaving.map_provider"
            @change="changeMapProvider('map_provider', $event)"
          >
            <option v-for="provider in MAP_PROVIDERS" :key="provider" :value="provider">
              {{ t(`settings.maps.${mapKey(provider)}`) }}
            </option>
          </select>
        </label>
        <p class="hint">{{ t('settings.maps.hint') }}</p>
        <label>
          {{ t('settings.maps.telegramLabel') }}
          <select
            :value="settings.map_provider_telegram ?? ''"
            :disabled="settingsSaving.map_provider_telegram"
            @change="changeMapProvider('map_provider_telegram', $event)"
          >
            <option value="">{{ t('settings.maps.sameAsAbove') }}</option>
            <option v-for="provider in MAP_PROVIDERS" :key="provider" :value="provider">
              {{ t(`settings.maps.${mapKey(provider)}`) }}
            </option>
          </select>
        </label>
        <p class="hint">{{ t('settings.maps.telegramHint') }}</p>
      </template>
    </section>

    <section v-if="sub?.key === 'hashtags'" class="card">
      <h3>{{ t('settings.tagStyles.title') }}</h3>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <template v-else-if="settings">
        <p class="hint">{{ t('settings.tagStyles.hint') }}</p>
        <TagStylesEditor
          :model-value="settings.tag_styles"
          :disabled="settingsSaving.tag_styles"
          @update:model-value="changeTagStyles"
        />
        <p class="hint">{{ t('settings.tagStyles.fallback') }}</p>
      </template>
    </section>

    <section v-if="sub?.key === 'bullets'" class="card">
      <h3>{{ t('settings.listBullets.title') }}</h3>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <template v-else-if="settings">
        <p class="hint">{{ t('settings.listBullets.intro') }}</p>
        <ListBulletsEditor
          :settings="settings"
          :disabled="settingsSaving.list_bullets"
          @change="changeBullets"
        />
      </template>
    </section>

    <section v-if="sub?.key === 'listview'" class="card">
      <h3>{{ t('settings.listView.title') }}</h3>
      <p class="hint">{{ t('settings.listView.intro') }}</p>
      <p>{{ listViewPrefs ? t('settings.listView.saved') : t('settings.listView.none') }}</p>
      <button
        type="button"
        :disabled="!listViewPrefs || settingsSaving.list_view"
        @click="resetListView"
      >
        {{ t('settings.listView.clear') }}
      </button>
    </section>

    <AccountsSection v-if="current?.key === 'accounts'" />

    <section v-if="current?.key === 'invites'" class="card">
      <h3>{{ t('settings.invites.title') }}</h3>
      <p class="hint">{{ t('settings.invites.intro') }}</p>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <p v-else-if="settingsError" class="error">{{ settingsError }}</p>
      <template v-else-if="settings">
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="settings.discoverable_by_username"
            :disabled="settingsSaving.discoverable_by_username"
            @change="toggleSetting('discoverable_by_username')"
          />
          {{ t('settings.invites.discoverable') }}
        </label>
        <p class="hint indent">{{ t('settings.invites.discoverableHint') }}</p>

        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="settings.reveal_invite_failure_reason"
            :disabled="settingsSaving.reveal_invite_failure_reason"
            @change="toggleSetting('reveal_invite_failure_reason')"
          />
          {{ t('settings.invites.reveal') }}
        </label>
        <p class="hint indent">{{ t('settings.invites.revealHint') }}</p>
      </template>
    </section>

    <section v-if="current?.key === 'notifications'" class="card">
      <h3>{{ t('settings.notifications.title') }}</h3>
      <p class="hint">{{ t('settings.notifications.intro') }}</p>
      <p v-if="settingsLoading">{{ t('common.loading') }}</p>
      <template v-else-if="settings">
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="settings.notify_new_todos"
            :disabled="settingsSaving.notify_new_todos"
            @change="toggleSetting('notify_new_todos')"
          />
          {{ t('settings.notifications.newTodos') }}
        </label>
        <p class="hint indent">{{ t('settings.notifications.newTodosHint') }}</p>

        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="settings.lightweight_confirmations"
            :disabled="settingsSaving.lightweight_confirmations"
            @change="toggleSetting('lightweight_confirmations')"
          />
          {{ t('settings.notifications.lightweight') }}
        </label>
        <p class="hint indent">{{ t('settings.notifications.lightweightHint') }}</p>

        <label v-if="settings.verbose_sync_enabled || auth.isAdmin" class="checkbox-label">
          <input
            type="checkbox"
            :checked="settings.verbose_sync_messages"
            :disabled="settingsSaving.verbose_sync_messages"
            @change="toggleSetting('verbose_sync_messages')"
          />
          {{ t('settings.notifications.verbose') }}
          <span v-if="!settings.verbose_sync_enabled" class="ui-chip ui-chip-accent">{{
            t('common.adminOnly')
          }}</span>
        </label>
        <p v-if="settings.verbose_sync_enabled || auth.isAdmin" class="hint indent">
          {{ t('settings.notifications.verboseHint') }}
        </p>
      </template>
    </section>

    <section v-if="current?.key === 'tags'" class="card">
      <TagsSection />
    </section>

    <SharingSection v-if="current?.key === 'blocked'" />

    <CalendarExport
      v-if="current?.key === 'export'"
      :valid-since="settings?.export_links_valid_since ?? null"
      @revoked="settings = $event"
    />

    <ProtocolHandlers v-if="sub?.key === 'links'" />

    <PostsSection v-if="sub?.key === 'posts'" />

    <section v-if="current?.key === 'tokens'" class="card">
      <h3>{{ t('settings.tokens.title') }}</h3>
      <p class="hint">{{ t('settings.tokens.hint') }}</p>
      <p v-if="settings?.last_api_used_at" class="hint">
        {{ t('settings.tokens.lastApiUse', { date: formatDateTime(settings.last_api_used_at) }) }}
      </p>

      <div v-if="revealedToken" class="token-reveal">
        <p>
          <strong>{{ t('settings.tokens.copyNow') }}</strong>
        </p>
        <code class="token-value">{{ revealedToken.token }}</code>
        <div class="reveal-actions">
          <button type="button" @click="copyToken">{{ t('common.copy') }}</button>
          <button type="button" class="danger" @click="revealedToken = null">
            {{ t('common.dismiss') }}
          </button>
        </div>
      </div>

      <form @submit.prevent="submitCreateToken" class="form-grid">
        <label>
          {{ t('common.name') }}
          <input v-model="tokenName" required :placeholder="t('settings.tokens.namePlaceholder')" />
        </label>
        <label>
          {{ t('settings.tokens.description') }}
          <input
            v-model="tokenDescription"
            :placeholder="t('settings.tokens.descriptionPlaceholder')"
          />
        </label>
        <label>
          {{ t('settings.tokens.expires') }}
          <input v-model="tokenExpiresAt" type="date" />
        </label>
      </form>
      <div class="scopes-field">
        <span class="hint">{{ t('settings.tokens.scopes') }}</span>
        <PermissionCheckboxGroup v-model="tokenScopes" :options="scopeOptions" />
      </div>
      <button
        type="button"
        :disabled="tokenCreating || !tokenName || tokenScopes.length === 0"
        @click="submitCreateToken"
      >
        {{ tokenCreating ? t('settings.tokens.creating') : t('settings.tokens.create') }}
      </button>
      <p v-if="tokenCreateError" class="error">{{ tokenCreateError }}</p>

      <p v-if="tokensLoading">{{ t('common.loading') }}</p>
      <p v-else-if="tokensError" class="error">{{ tokensError }}</p>
      <p v-else-if="tokens.length === 0" class="hint">{{ t('settings.tokens.none') }}</p>
      <ul v-else class="token-list">
        <li v-for="token in tokens" :key="token.id">
          <div class="token-info">
            <strong>{{ token.name }}</strong>
            <span v-if="token.revoked_at" class="ui-chip">{{ t('settings.tokens.revoked') }}</span>
            <span class="hint">{{ token.scopes.join(', ') }}</span>
            <span class="hint">{{
              token.last_used_at
                ? t('settings.tokens.lastUsed', { date: formatDateTime(token.last_used_at) })
                : t('settings.tokens.neverUsed')
            }}</span>
          </div>
          <button
            v-if="!token.revoked_at"
            type="button"
            class="danger"
            :disabled="revokingId === token.id"
            @click="revoke(token)"
          >
            {{
              revokingId === token.id ? t('settings.tokens.revoking') : t('settings.tokens.revoke')
            }}
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.entry-title {
  flex: 1;
  min-width: 0;
}

.language-row {
  width: 100%;
  text-align: left;
  color: var(--tg-text-color);
  background: none;
  border: 0;
  font: inherit;
}

.language-icon {
  display: inline-flex;
  justify-content: center;
  width: 1.6rem;
  font-size: 1.2rem;
  line-height: 1;
}

.check {
  color: var(--tg-link-color);
  font-size: 1.2rem;
}

/* A category page has its title in the header already. */
.single > .card > h3:first-child,
.single :deep(.group-title) {
  display: none;
}

.card {
  background: var(--tg-section-bg-color);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.checkbox-label input {
  width: auto;
}

.hint {
  color: var(--tg-hint-color);
  font-size: 0.85rem;
}

.indent {
  margin: 0.15rem 0 0.75rem 1.75rem;
}

.error {
  color: var(--tg-destructive-text-color);
}

.form-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  align-items: end;
  margin-bottom: 0.75rem;
}

.form-grid label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--tg-section-header-text-color);
}

.form-grid input {
  padding: 0.5rem;
  border: 1px solid var(--tg-secondary-bg-color);
  border-radius: 0.5rem;
  background: var(--tg-bg-color);
  color: var(--tg-text-color);
}

button {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--tg-button-color);
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
  cursor: pointer;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button.danger {
  background: transparent;
  color: var(--tg-destructive-text-color);
  border-color: var(--tg-secondary-bg-color);
}

.card h4 {
  margin: 1rem 0 0.35rem;
  font-size: 1rem;
}

.user-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.user-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-top: 1px solid var(--tg-secondary-bg-color);
}

.user-list li:first-child {
  border-top: none;
}

.token-reveal {
  background: var(--tg-bg-color);
  border: 1px solid var(--tg-button-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

.token-value {
  display: block;
  word-break: break-all;
  padding: 0.5rem;
  margin: 0.5rem 0;
  background: var(--tg-secondary-bg-color);
  border-radius: 0.35rem;
}

.reveal-actions {
  display: flex;
  gap: 0.5rem;
}

.scopes-field {
  margin: 0.5rem 0 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.token-list {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.token-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-top: 1px solid var(--tg-secondary-bg-color);
  gap: 0.5rem;
}

.token-list li:first-child {
  border-top: none;
}

.token-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
</style>
